/**
 * Envoltorio mínimo de IndexedDB.
 *
 * Sin librería: lo que necesitamos son cuatro operaciones sobre dos almacenes,
 * y una dependencia menos es una superficie menos que mantener en una app que
 * va a vivir años sin que nadie la toque.
 *
 * IndexedDB y no localStorage porque hay que guardar Blobs (las fotos) y porque
 * localStorage es síncrono: bloquear el hilo principal mientras se escriben
 * 200 KB de foto se nota al pulsar el botón.
 */

const NOMBRE_DB = 'gextia-gastos';
const VERSION = 1;

export const ALMACEN_ACCIONES = 'acciones';
export const ALMACEN_REFERENCIAS = 'referencias';
export const ALMACEN_LOCALES = 'locales';
export const ALMACEN_CACHE = 'cache';

let promesaDb: Promise<IDBDatabase> | null = null;

function abrir(): Promise<IDBDatabase> {
  if (promesaDb) return promesaDb;

  promesaDb = new Promise((resolver, rechazar) => {
    const peticion = indexedDB.open(NOMBRE_DB, VERSION);

    peticion.onupgradeneeded = () => {
      const db = peticion.result;

      if (!db.objectStoreNames.contains(ALMACEN_ACCIONES)) {
        // Sin índice de orden: se ordena en memoria por el campo `orden`, que
        // es un contador estricto. Un índice sobre la marca de tiempo no vale,
        // porque varias acciones caen en el mismo milisegundo y el desempate
        // acabaría siendo el id aleatorio (ver cola.ts).
        db.createObjectStore(ALMACEN_ACCIONES, { keyPath: 'id' });
      }

      if (!db.objectStoreNames.contains(ALMACEN_REFERENCIAS)) {
        db.createObjectStore(ALMACEN_REFERENCIAS, { keyPath: 'idLocal' });
      }

      if (!db.objectStoreNames.contains(ALMACEN_LOCALES)) {
        const locales = db.createObjectStore(ALMACEN_LOCALES, { keyPath: 'idLocal' });
        locales.createIndex('padre', 'padre');
      }

      if (!db.objectStoreNames.contains(ALMACEN_CACHE)) {
        db.createObjectStore(ALMACEN_CACHE, { keyPath: 'clave' });
      }
    };

    peticion.onsuccess = () => resolver(peticion.result);
    peticion.onerror = () => rechazar(peticion.error);
  });

  return promesaDb;
}

function promesaDePeticion<T>(peticion: IDBRequest<T>): Promise<T> {
  return new Promise((resolver, rechazar) => {
    peticion.onsuccess = () => resolver(peticion.result);
    peticion.onerror = () => rechazar(peticion.error);
  });
}

export async function guardar<T>(almacen: string, valor: T): Promise<void> {
  const db = await abrir();
  const tx = db.transaction(almacen, 'readwrite');
  await promesaDePeticion(tx.objectStore(almacen).put(valor));
}

export async function obtener<T>(almacen: string, clave: IDBValidKey): Promise<T | undefined> {
  const db = await abrir();
  const tx = db.transaction(almacen, 'readonly');
  return promesaDePeticion(tx.objectStore(almacen).get(clave) as IDBRequest<T | undefined>);
}

export async function todos<T>(almacen: string, indice?: string): Promise<T[]> {
  const db = await abrir();
  const tx = db.transaction(almacen, 'readonly');
  const origen = indice
    ? tx.objectStore(almacen).index(indice)
    : tx.objectStore(almacen);
  return promesaDePeticion(origen.getAll() as IDBRequest<T[]>);
}

export async function borrar(almacen: string, clave: IDBValidKey): Promise<void> {
  const db = await abrir();
  const tx = db.transaction(almacen, 'readwrite');
  await promesaDePeticion(tx.objectStore(almacen).delete(clave));
}

export async function vaciar(almacen: string): Promise<void> {
  const db = await abrir();
  const tx = db.transaction(almacen, 'readwrite');
  await promesaDePeticion(tx.objectStore(almacen).clear());
}
